import { DY_CENTER, el, escapeXml } from "./svg-builder.js";

/** The subset of a resolved node style that drives the annotation badge. */
interface BadgeStyle {
  badgeIcon?: string;
  badgeLabel?: string;
  badgeColor?: string;
}

/**
 * Build the SVG children of a single annotation badge (a colored circle, an
 * optional icon glyph, and an optional label) centred on (`badgeX`, `badgeY`).
 *
 * Returns an empty array when the style carries neither an icon nor a label, so
 * callers can render a badge unconditionally. The caller wraps the returned
 * children in its own `<g>` — the org renderers wrap them plainly.
 *
 * This circle is the **org card's** badge. The system and deploy cards moved to
 * the inset pill of `corner-lane.ts` (#2420), because there the badge shared
 * its corner with the i / D buttons and floated outside the card into incoming
 * edges. An org card has neither problem: no buttons, and the badge already
 * sits inside the card (the callers pass card-relative coordinates). Sharing
 * one geometry across both (#1583) would mean giving org cards a lane they
 * have no residents for.
 *
 * `iconOnly` renders just the colored glyph (no trailing label) so the badge
 * stays inside a compact, grid-packed card — the label would otherwise extend
 * past the card's right edge into its neighbor. The system diagram, which has
 * margin to the right of a node, keeps the label.
 */
export function badgeChildren(
  style: BadgeStyle,
  badgeX: number,
  badgeY: number,
  fallbackColor: string,
  iconOnly = false,
): string[] {
  if (!style.badgeIcon && !style.badgeLabel) return [];
  const children: string[] = [];
  const badgeColor = style.badgeColor ?? fallbackColor;
  children.push(el("circle", { cx: badgeX, cy: badgeY, r: 10, fill: badgeColor }));
  if (style.badgeIcon) {
    children.push(
      el(
        "text",
        {
          x: badgeX,
          y: badgeY,
          "text-anchor": "middle",
          dy: DY_CENTER,
          fill: "white",
          "font-size": "10px",
        },
        escapeXml(style.badgeIcon),
      ),
    );
  }
  if (!iconOnly && style.badgeLabel) {
    children.push(
      el(
        "text",
        {
          x: badgeX + 14,
          y: badgeY,
          dy: DY_CENTER,
          fill: badgeColor,
          "font-size": "9px",
          "font-weight": "bold",
          "font-family": "sans-serif",
        },
        escapeXml(style.badgeLabel),
      ),
    );
  }
  return children;
}
